/**
 * Les modules de Gertrude, en un seul endroit.
 *
 * SOURCE UNIQUE : le menu latéral ET les cartes de la page d'accueil lisent
 * cette liste. Deux listes séparées divergent au premier module ajouté — on
 * l'ajoute au menu, on oublie la carte, et le module devient introuvable pour
 * qui passe par l'accueil.
 *
 * Ce qui est décrit ici ne fait qu'ORIENTER : chaque route revérifie les droits
 * côté serveur. Masquer une entrée ne protège rien.
 */

export interface SubItem {
  to: string;
  label: string;
  show: boolean;
}

export interface ModuleDef {
  key: string;
  label: string;
  icon: string;
  to: string; // landing par défaut (1er sous-item accessible)
  show: boolean;
  sub: SubItem[];
  /** Une ligne, pour la carte d'accueil : ce que le module sert à faire. */
  description: string;
}

export interface DroitsNdf {
  peutDeclarer: boolean;
  peutGererFiches: boolean;
  peutAdministrer: boolean;
  peutValider: boolean;
  aAcces: boolean;

  /** Congés — mêmes droits, autre module (voir `useNdfMoi`). */
  congesDeclarer: boolean;
  /**
   * Concerné par les congés, c'est-à-dire salarié, alternant ou stagiaire.
   * Faux pour un mandataire social : il peut valider ou administrer le module
   * sans y avoir de compteurs, d'arrêts ni de tickets.
   */
  congesConcerne: boolean;
  congesValider: boolean;
  congesApprouverRh: boolean;
  congesAbsencesRh: boolean;
  congesTicketsPaie: boolean;
  congesAdministrer: boolean;
}

/**
 * EN APERÇU DE RÔLE, on n'appelle PAS cette fonction avec des droits figés.
 *
 * `/api/ndf/moi` répond déjà pour la personne endossée : les droits `ndf`
 * passés ici décrivent donc bien ce qu'ELLE peut faire, et le menu se construit
 * tout seul. Une version antérieure remplaçait la liste par une entrée
 * « Salariés » unique : le testeur endossait une collaboratrice et se
 * retrouvait avec le seul écran qu'elle n'a justement pas le droit de voir,
 * sans ses dépenses ni ses notes.
 *
 * En revanche, `isAdmin` et `isValidator` viennent de la SESSION, donc du
 * compte réel : l'appelant doit les passer à `false` pendant un aperçu, sinon
 * on prêterait à la personne observée les factures et les paiements de
 * l'observateur.
 */
export function buildModules(
  isAdmin: boolean,
  isValidator: boolean,
  ndf: DroitsNdf,
): ModuleDef[] {
  // « À payer » et « En cours » : leurs routes et leur API sont réservées à
  // l'admin. Les montrer à un valideur non admin le menait sur un refus.
  const canAPayer = isAdmin;
  const canEnCours = isAdmin;

  const raw: Omit<ModuleDef, 'to'>[] = [
    {
      key: 'factures',
      label: 'Factures fournisseurs',
      icon: '📄',
      description: 'Valider les factures extraites, les créer dans Books, les archiver.',
      show: isAdmin,
      sub: [
        { to: '/factures/dashboard', label: 'Dashboard', show: isAdmin },
        { to: '/factures/liste', label: 'Factures', show: isAdmin },
        { to: '/factures/fournisseurs', label: 'Fournisseurs', show: isAdmin },
      ],
    },
    {
      key: 'auto-facturation',
      label: 'Auto-facturation',
      icon: '🔄',
      description: 'Facturer les experts à partir des sessions Conseil du CRM.',
      show: isAdmin,
      sub: [
        { to: '/auto-facturation/nouvelle', label: 'Nouvelle facturation', show: isAdmin },
        { to: '/auto-facturation/historique', label: 'Historique', show: isAdmin },
      ],
    },
    {
      key: 'exports-comptables',
      label: 'Exports comptables',
      icon: '📑',
      description: 'Produire les rapports mensuels remis à KPMG, et les déposer dans Dropbox.',
      show: isAdmin,
      sub: [
        { to: '/exports-comptables', label: 'Clôtures mensuelles', show: isAdmin },
        { to: '/exports-comptables/initialisation', label: 'Initialisation du registre', show: isAdmin },
      ],
    },
    {
      key: 'paiements',
      label: 'Paiements',
      icon: '💳',
      description: 'Préparer les virements fournisseurs, les faire valider, les exporter.',
      show: isAdmin || isValidator,
      sub: [
        { to: '/paiements/a-payer', label: 'À payer', show: canAPayer },
        { to: '/paiements/a-valider', label: 'À valider', show: isAdmin || isValidator },
        { to: '/paiements/remboursements', label: 'Remboursements', show: isAdmin },
        { to: '/paiements/exporter', label: 'Exporter', show: isAdmin },
        { to: '/paiements/en-cours', label: 'En cours', show: canEnCours },
        { to: '/paiements/beneficiaires', label: 'Bénéficiaires', show: isAdmin },
        { to: '/paiements/parametres', label: 'Paramètres', show: isAdmin },
      ],
    },
    {
      key: 'conges',
      // Le nom du cadrage : le module ne porte pas que les congés, il porte
      // aussi les arrêts de travail, les tickets et la préparation de la paie.
      label: 'Congés & Absences',
      icon: '🌴',
      description: 'Poser ses congés, suivre ses soldes, faire valider ses absences.',
      show: ndf.congesDeclarer || ndf.congesValider || ndf.congesAbsencesRh || ndf.congesAdministrer,
      // On n'annonce QUE les écrans qui existent : une entrée de menu qui mène
      // à une page blanche use la confiance plus vite qu'une fonctionnalité
      // manquante. Le paramétrage, lui, a quitté ce module pour la section
      // Paramètres (R-SOC-11).
      //
      // Le module est donc INVISIBLE aujourd'hui — sans sous-entrée visible,
      // `buildModules` l'écarte de lui-même, du menu comme des cartes.
      //
      // Le Lot 2 a donné au module sa première destination (« Mes congés »),
      // le Lot 3 y a ajouté le calendrier et les demandes ; « À valider », le
      // calendrier d'équipe, le registre, les compteurs, les absences RH, les
      // tickets et la paie ont suivi jusqu'au Lot 8. Tout est là, et reste
      // invisible tant qu'aucun profil ne porte de droit `conges`.
      sub: [
        // Les trois onglets PERSONNELS : ils demandent le droit de déclarer ET
        // d'être concerné. Un mandataire social n'a pas de congés à poser ni de
        // solde à consulter ; ces écrans lui auraient montré des zéros. Ses
        // onglets de profil, eux, sont plus bas et ne dépendent que de lui.
        {
          to: '/conges/mes-conges',
          label: 'Mes congés',
          show: ndf.congesDeclarer && ndf.congesConcerne,
        },
        {
          to: '/conges/calendrier',
          label: 'Mon calendrier',
          show: ndf.congesDeclarer && ndf.congesConcerne,
        },
        {
          to: '/conges/demandes',
          label: 'Mes demandes',
          show: ndf.congesDeclarer && ndf.congesConcerne,
        },
        // Placé haut chez un valideur : c'est ce qu'il vient faire, et ce qui
        // retient quelqu'un d'autre tant qu'il ne l'a pas fait.
        { to: '/conges/a-valider', label: 'À valider', show: ndf.congesValider },
        // Un seul écran pour l'équipe et pour l'entreprise : c'est le serveur
        // qui décide du périmètre, et le titre s'ajuste à ce qu'il rend.
        {
          to: '/conges/calendrier-equipe',
          label: 'Calendrier d’équipe',
          show: ndf.congesValider || ndf.congesAbsencesRh || ndf.congesAdministrer,
        },
        // Le registre : sans lui, une demande déjà tranchée n'était consultable
        // nulle part, et l'annulation RH d'une absence approuvée restait
        // inatteignable dans l'interface.
        {
          to: '/conges/registre',
          label: 'Demandes',
          show: ndf.congesValider || ndf.congesAbsencesRh || ndf.congesAdministrer,
        },
        // ── Onglets de PILOTAGE RH, après les onglets du salarié ──
        { to: '/conges/compteurs', label: 'Compteurs', show: ndf.congesApprouverRh },
        { to: '/conges/arrets', label: 'Arrêts de travail', show: ndf.congesAbsencesRh },
        // Les justificatifs que la loi ne delivre qu'apres l'evenement : c'est
        // la RH qui les verifie, donc le meme droit que les compteurs.
        { to: '/conges/justificatifs', label: 'Justificatifs', show: ndf.congesApprouverRh },
        { to: '/conges/tickets', label: 'Tickets restaurant', show: ndf.congesTicketsPaie },
        { to: '/conges/paie', label: 'Envoi paie', show: ndf.congesTicketsPaie },
        // La reprise 2026 est un écran de MISE EN SERVICE, joué une fois. Il
        // reste ensuite accessible : c'est lui qui permettra d'expliquer un
        // écart de solde six mois plus tard.
        { to: '/conges/reprise', label: 'Reprise 2026', show: ndf.congesAdministrer },
      ],
    },
    {
      key: 'notes-de-frais',
      label: 'Notes de frais',
      icon: '🧾',
      description: 'Déclarer ses dépenses, monter ses notes, les faire approuver.',
      // Visible dès qu'on a un droit dans le module — et non plus aux seuls
      // administrateurs : un collaborateur doit pouvoir atteindre ses dépenses.
      show: ndf.peutDeclarer || ndf.peutGererFiches || ndf.peutAdministrer || ndf.peutValider,
      sub: [
        { to: '/notes-de-frais/tableau-de-bord', label: 'Tableau de bord', show: ndf.peutDeclarer || ndf.peutValider },
        { to: '/notes-de-frais/mes-depenses', label: 'Mes dépenses', show: ndf.peutDeclarer },
        { to: '/notes-de-frais/mes-notes', label: 'Mes notes de frais', show: ndf.peutDeclarer },
        // Placé haut : c'est ce qu'un valideur vient faire, et ce qui bloque
        // le remboursement de quelqu'un d'autre tant qu'il ne l'a pas fait.
        { to: '/notes-de-frais/a-valider', label: 'À valider', show: ndf.peutValider },
        { to: '/notes-de-frais/pilotage', label: 'Pilotage', show: ndf.peutAdministrer },
        // « Moi » n'existait que dans la barre d'onglets du mobile, masquée
        // au-delà de `md` : sur ordinateur, le récapitulatif kilométrique et
        // le bac à sable de pré-production n'avaient AUCUN point d'entrée.
        { to: '/notes-de-frais/moi', label: 'Moi', show: ndf.aAcces },
      ],
    },
    {
      // Le référentiel des salariés sert DEUX modules : il n'avait aucune raison
      // de vivre sous l'un d'eux. Il est ici un module à part entière, rangé
      // dans la section Ressources Humaines côté administration (R-SOC-11).
      key: 'salaries',
      label: 'Salariés',
      icon: '👥',
      description: 'Le référentiel des fiches, partagé par les notes de frais et les congés.',
      show: ndf.peutGererFiches,
      sub: [
        { to: '/ressources-humaines/salaries', label: 'Fiches salariés', show: ndf.peutGererFiches },
        // Tickets restaurant (Lot 7), Primes et Envoi paie (Lot 8) viendront ici.
      ],
    },
    // La section Paramètres contient TROIS modules, et non un module à trois
    // sous-entrées : sans quoi le menu n'affiche qu'une ligne « Paramètres » et
    // les trois destinations deviennent des onglets d'une page intermédiaire.
    // Chacune doit s'atteindre depuis le menu, et garder SES propres onglets
    // internes une fois ouverte (R-SOC-11).
    {
      key: 'parametrage-conges',
      label: 'Paramétrage Congés & Absences',
      icon: '🌴',
      description: 'Types d’absence, congés exceptionnels et valeurs de calcul.',
      show: ndf.congesAdministrer,
      sub: [{ to: '/conges/parametrage', label: 'Paramétrage Congés & Absences', show: ndf.congesAdministrer }],
    },
    {
      key: 'parametrage-ndf',
      label: 'Paramétrage Notes de frais',
      icon: '🧾',
      description: 'Barème kilométrique, catégories, comptes analytiques et seuils.',
      show: ndf.peutAdministrer,
      sub: [
        { to: '/notes-de-frais/parametrage', label: 'Paramétrage Notes de frais', show: ndf.peutAdministrer },
      ],
    },
    {
      // Les écrans d'administration des accès sont les SOUS-ONGLETS de ce
      // module. Ils étaient auparavant traités en cas particulier dans le
      // Layout, et venaient alors remplacer les onglets principaux.
      key: 'utilisateurs',
      label: 'Utilisateurs',
      icon: '🔑',
      description: 'Comptes, profils et rôles.',
      show: isAdmin,
      sub: [
        { to: '/admin/utilisateurs', label: 'Utilisateurs', show: isAdmin },
        { to: '/admin/profils', label: 'Profils et rôles', show: isAdmin },
      ],
    },
    {
      key: 'support',
      label: 'Support',
      icon: '🛟',
      description: 'Les alertes techniques remontées par l’application.',
      show: isAdmin,
      sub: [{ to: '/support/tickets-dev', label: 'Tickets dev', show: isAdmin }],
    },
  ];

  return raw
    .map((m) => {
      const firstVisible = m.sub.find((s) => s.show);
      return { ...m, to: firstVisible?.to ?? '#' };
    })
    .filter((m) => m.show && m.sub.some((s) => s.show));
}

// ═══════════════════════════════════════════════════════════════════════════
// NAVIGATION À DEUX NIVEAUX (R-SOC-11)
// ═══════════════════════════════════════════════════════════════════════════

export interface SectionDef {
  cle: string;
  libelle: string;
  icone: string;
  /** Les modules rangés dans cette section, déjà filtrés par les droits. */
  modules: ModuleDef[];
  /** Une section sans sous-menu mène directement quelque part (Support). */
  direct?: boolean;
}

/**
 * Un profil RH ou ADMIN voit-il la navigation en sections ?
 *
 * Le regroupement ne sert que ceux qui portent plusieurs casquettes : sept
 * modules à plat deviennent illisibles. Un collaborateur ou un manager, qui
 * n'en a que deux, y perdrait un niveau de clic pour rien — leur navigation
 * reste STRICTEMENT celle d'avant (décision D3.2 amendée).
 */
export function navigationParSections(isAdmin: boolean, ndf: DroitsNdf): boolean {
  return isAdmin || ndf.peutGererFiches || ndf.peutAdministrer || ndf.congesAdministrer;
}

/** Dans quelle section chaque module se range. */
const RANGEMENT: Record<string, string> = {
  factures: 'finance',
  'auto-facturation': 'finance',
  'exports-comptables': 'finance',
  paiements: 'finance',
  'notes-de-frais': 'ressources-humaines',
  conges: 'ressources-humaines',
  salaries: 'ressources-humaines',
  'parametrage-conges': 'parametres',
  'parametrage-ndf': 'parametres',
  utilisateurs: 'parametres',
  support: 'support',
};

const SECTIONS: Array<{ cle: string; libelle: string; icone: string; direct?: boolean }> = [
  { cle: 'finance', libelle: 'Finance', icone: '💶' },
  { cle: 'ressources-humaines', libelle: 'Ressources Humaines', icone: '👥' },
  // Une seule destination : la replier n'aurait rien à cacher.
  { cle: 'support', libelle: 'Support', icone: '🛟', direct: true },
  { cle: 'parametres', libelle: 'Paramètres', icone: '⚙️' },
];

/**
 * Les mêmes modules, rangés en sections.
 *
 * Construits à partir de `buildModules` et non d'une seconde liste : ajouter un
 * module doit l'ajouter partout, ou nulle part. Une section vide de modules
 * accessibles n'est pas rendue.
 */
export function buildSections(
  isAdmin: boolean,
  isValidator: boolean,
  ndf: DroitsNdf,
): SectionDef[] {
  const modules = buildModules(isAdmin, isValidator, ndf);
  return SECTIONS.map((s) => ({
    ...s,
    modules: modules.filter((m) => RANGEMENT[m.key] === s.cle),
  })).filter((s) => s.modules.length > 0);
}
